/**
 * SPT — Shop by Product Type
 * Web Components:
 *   - <product-slider>  — tab switching + slider navigation + drag/swipe
 *   - <product-compare> — compare modal with search + table
 *
 * Cart: Shopify Ajax cart/add.js + Bundled Section Rendering for live drawer & icon updates.
 */

/* ============================================================
   CART HELPERS
   ============================================================ */

/**
 * Detect which Dawn section IDs we need for cart drawer + header icon.
 * Reads directly from the DOM so it works on any Dawn version.
 */
function sptGetCartSectionIds() {
  const ids = new Set();

  // Cart drawer section wrapper
  const drawerWrapper = document.querySelector(
    '[id^="shopify-section-"][id*="cart-drawer"], #shopify-section-cart-drawer'
  );
  if (drawerWrapper) {
    ids.add(drawerWrapper.id.replace('shopify-section-', ''));
  } else {
    ids.add('cart-drawer');
  }

  // Cart icon bubble section
  const iconBubbleWrapper = document.querySelector(
    '[id^="shopify-section-"][id*="cart-icon-bubble"], #shopify-section-cart-icon-bubble'
  );
  if (iconBubbleWrapper) {
    ids.add(iconBubbleWrapper.id.replace('shopify-section-', ''));
  } else {
    ids.add('cart-icon-bubble');
  }

  // Header section wrapper (contains cart icon bubble)
  const headerWrapper = document.querySelector(
    '[id^="shopify-section-"][id*="header"], #shopify-section-header'
  );
  if (headerWrapper) {
    ids.add(headerWrapper.id.replace('shopify-section-', ''));
  }

  return [...ids];
}

/**
 * After cart/add.js succeeds: fetch fresh section HTML via Bundled Section Rendering,
 * then patch the cart icon count + cart drawer without a page reload.
 */
async function sptRefreshCart(sourceButton) {
  const cartRoot   = window.Shopify?.routes?.root || '/';
  const sectionIds = sptGetCartSectionIds();

  const res  = await fetch(`${cartRoot}?sections=${sectionIds.join(',')}`);
  const data = await res.json();

  sptPatchCartCount(data.item_count, data.sections);
  sptPatchAndOpenCartDrawer(data.sections, sourceButton);
}

/**
 * Update cart icon bubble count.
 * First tries DOM-patching from fresh section HTML; falls back to manual writes.
 */
function sptPatchCartCount(itemCount, sections) {
  // Try to find and update cart icon bubble section
  const iconBubbleKey = Object.keys(sections || {}).find(k => k.includes('cart-icon-bubble'));
  
  if (iconBubbleKey && sections[iconBubbleKey]) {
    const iconBubbleWrapper = document.querySelector(`#shopify-section-${iconBubbleKey}`);
    if (iconBubbleWrapper) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections[iconBubbleKey];
      const newBubble = tmp.querySelector('.cart-count-bubble');
      const oldBubble = iconBubbleWrapper.querySelector('.cart-count-bubble');
      if (newBubble && oldBubble) {
        oldBubble.replaceWith(newBubble);
        return; // done — fresh HTML injected
      }
    }
  }

  // Also try header section
  const headerKey = Object.keys(sections || {}).find(k => k.includes('header'));
  if (headerKey && sections[headerKey]) {
    const headerWrapper = document.querySelector(`#shopify-section-${headerKey}`);
    if (headerWrapper) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections[headerKey];
      const newBubble = tmp.querySelector('.cart-count-bubble');
      const oldBubble = headerWrapper.querySelector('.cart-count-bubble');
      if (newBubble && oldBubble) {
        oldBubble.replaceWith(newBubble);
        return; // done — fresh HTML injected
      }
    }
  }

  // Fallback: manually write count
  document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
    const spans = bubble.querySelectorAll('span');
    if (spans[0]) spans[0].textContent = itemCount;
    if (spans[1]) spans[1].textContent = `${itemCount} items`;
    bubble.classList.toggle('hidden', itemCount === 0);
  });
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = itemCount;
  });
}

/**
 * Inject fresh cart-drawer HTML, then open it.
 */
function sptPatchAndOpenCartDrawer(sections, sourceButton) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  const drawerKey = Object.keys(sections || {}).find(
    k => k.includes('cart-drawer') || k === 'cart-drawer'
  );

  if (drawerKey && sections[drawerKey]) {
    const tmp = document.createElement('div');
    tmp.innerHTML = sections[drawerKey];

    // Update the drawer inner content
    const newDrawerInner = tmp.querySelector('.drawer__inner');
    const currentDrawerInner = cartDrawer.querySelector('.drawer__inner');
    
    if (newDrawerInner && currentDrawerInner) {
      currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      
      // Remove is-empty class if cart has items
      currentDrawerInner.classList.remove('is-empty');
      cartDrawer.classList.remove('is-empty');
    }
  }

  // Open the drawer
  if (typeof cartDrawer.open === 'function') {
    cartDrawer.open(sourceButton);
  } else {
    // Older Dawn fallback
    cartDrawer.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    const overlay = document.querySelector('cart-drawer-overlay, .overlay');
    if (overlay) overlay.classList.add('active');
  }
}

/**
 * Main add-to-cart — called by both slider cards and compare popup.
 */
async function sptAddToCart(variantId, buttonEl) {
  if (!variantId || buttonEl.disabled) return;

  const textEl      = buttonEl.querySelector('.spt__btn-text');
  const originalTxt = textEl?.textContent.trim() || 'Add to Cart';
  const cartRoot    = window.Shopify?.routes?.root || '/';

  buttonEl.classList.add('spt__btn--loading');
  buttonEl.disabled = true;

  try {
    // 1. Add item to cart with section rendering
    const sectionIds = sptGetCartSectionIds();
    const addRes = await fetch(`${cartRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(',')
      })
    });

    if (!addRes.ok) {
      const errData = await addRes.json().catch(() => ({}));
      throw new Error(errData.description || 'Could not add item to cart.');
    }

    const addData = await addRes.json();

    // 2. Update cart icon count
    sptPatchCartCount(addData.item_count || 0, addData.sections);

    // 3. Update and open cart drawer
    sptPatchAndOpenCartDrawer(addData.sections, buttonEl);

    // 4. Button success feedback
    if (textEl) {
      textEl.textContent = '✓ Added!';
      setTimeout(() => { textEl.textContent = originalTxt; }, 2200);
    }

  } catch (err) {
    console.error('[SPT] Add to cart error:', err);
    if (textEl) {
      textEl.textContent = 'Error — try again';
      setTimeout(() => { textEl.textContent = originalTxt; }, 2500);
    }
  } finally {
    buttonEl.classList.remove('spt__btn--loading');
    buttonEl.disabled = false;
  }
}


/* ============================================================
   <product-slider> Web Component
   ============================================================ */
class ProductSlider extends HTMLElement {
  constructor() {
    super();
    this._isDragging   = false;
    this._startX       = 0;
    this._startScrollLeft = 0;
    this._mouseMoved   = 0;
  }

  connectedCallback() {
    this._track   = this.querySelector('.spt__track');
    this._wrapper = this.querySelector('.spt__track-wrapper');
    this._prevBtn = this.querySelector('.spt__arrow--prev');
    this._nextBtn = this.querySelector('.spt__arrow--next');
    this._cards   = Array.from(this._track?.querySelectorAll('.spt__card') || []);

    if (!this._track || this._cards.length === 0) return;

    this._bindArrows();
    this._bindDrag();
    this._bindAddToCart();
    this._updateArrows();

    this.classList.add('spt-slider-ready');
    
    // Update arrows on scroll
    this._wrapper.addEventListener('scroll', () => this._updateArrows());
  }

  _bindArrows() {
    this._prevBtn?.addEventListener('click', () => this._slide(-1));
    this._nextBtn?.addEventListener('click', () => this._slide(1));
  }

  _slide(direction) {
    const cardWidth = this._cardWidth();
    const currentScroll = this._wrapper.scrollLeft;
    const targetScroll = currentScroll + (direction * cardWidth);
    
    // Use smooth scrollTo for animated transition
    this._wrapper.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }

  _visibleCount() {
    if (!this._wrapper) return 4;
    const w = this._wrapper.offsetWidth;
    if (w <= 599)  return 1;
    if (w <= 899)  return 2;
    if (w <= 1199) return 3;
    return 4;
  }

  _cardWidth() {
    const card = this._cards[0];
    if (!card) return 0;
    const gap = parseInt(getComputedStyle(this._track).gap) || 20;
    return card.offsetWidth + gap;
  }

  _updateArrows() {
    if (!this._wrapper || !this._prevBtn || !this._nextBtn) return;

    const scrollLeft = this._wrapper.scrollLeft;
    const maxScroll = this._wrapper.scrollWidth - this._wrapper.clientWidth;

    // If there are no extra cards to slide, hide both buttons
    if (maxScroll <= 10) {
      this._prevBtn.hidden = true;
      this._nextBtn.hidden = true;

      this._prevBtn.disabled = true;
      this._nextBtn.disabled = true;
      return;
    }

    // If slider has extra cards, show both buttons
    this._prevBtn.hidden = false;
    this._nextBtn.hidden = false;

    // But disable button when that side has no more cards
    this._prevBtn.disabled = scrollLeft <= 10;
    this._nextBtn.disabled = Math.ceil(scrollLeft) >= maxScroll - 10;
  }

  _bindDrag() {
    const w = this._wrapper;
    if (!w) return;
    
    // Keep mouse drag for desktop
    w.addEventListener('mousedown',  e => this._onDragStart(e));
    w.addEventListener('mousemove',  e => this._onDragMove(e));
    w.addEventListener('mouseup',    e => this._onDragEnd(e));
    w.addEventListener('mouseleave', e => this._onDragEnd(e));
    
    // Touch swipe for mobile
    w.addEventListener('touchstart', e => this._onDragStart(e), { passive: true });
    w.addEventListener('touchmove',  e => this._onDragMove(e),  { passive: true });
    w.addEventListener('touchend',   e => this._onDragEnd(e));
  }

  _getX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }

  _onDragStart(e) {
    this._isDragging = true;
    this._startX = this._getX(e);
    this._startScrollLeft = this._wrapper.scrollLeft;
    this._mouseMoved = 0;
    this._wrapper.style.scrollBehavior = 'auto'; // Disable smooth scroll during drag
  }

  _onDragMove(e) {
    if (!this._isDragging) return;
    const currentX = this._getX(e);
    this._mouseMoved = this._startX - currentX;
    this._wrapper.scrollLeft = this._startScrollLeft + this._mouseMoved;
  }

  _onDragEnd() {
    if (!this._isDragging) return;
    this._isDragging = false;
    this._wrapper.style.scrollBehavior = 'smooth'; // Re-enable smooth scroll
    this._updateArrows();
    this._mouseMoved = 0;
  }

  _bindAddToCart() {
    this.addEventListener('click', e => {
      const btn = e.target.closest('.spt__btn--cart');
      if (!btn) return;
      sptAddToCart(btn.dataset.variantId, btn);
    });
  }
}

customElements.define('product-slider', ProductSlider);


/* ============================================================
   <product-compare> Web Component
   ============================================================ */
class ProductCompare extends HTMLElement {
  constructor() {
    super();
    this._productA      = null;
    this._productB      = null;
    this._searchTimeout = null;
  }

  connectedCallback() {
    this._closeBtn     = this.querySelector('.spt-compare__close');
    this._backdrop     = this.querySelector('.spt-compare__backdrop');
    this._searchInput  = this.querySelector('.spt-compare__search-input');
    this._resultsList  = this.querySelector('.spt-compare__results');
    this._searchPanel  = this.querySelector('.spt-compare__search-panel');
    this._tableWrapper = this.querySelector('.spt-compare__table-wrapper');

    this._cells = {
      imgA:     this.querySelector('#spt-cmp-img-a'),
      imgB:     this.querySelector('#spt-cmp-img-b'),
      nameA:    this.querySelector('#spt-cmp-name-a'),
      nameB:    this.querySelector('#spt-cmp-name-b'),
      priceA:   this.querySelector('#spt-cmp-price-a'),
      priceB:   this.querySelector('#spt-cmp-price-b'),
      availA:   this.querySelector('#spt-cmp-avail-a'),
      availB:   this.querySelector('#spt-cmp-avail-b'),
      ratingA:  this.querySelector('#spt-cmp-rating-a'),
      ratingB:  this.querySelector('#spt-cmp-rating-b'),
      actionsA: this.querySelector('#spt-cmp-actions-a'),
      actionsB: this.querySelector('#spt-cmp-actions-b'),
    };

    this._closeBtn?.addEventListener('mousedown', e => { e.preventDefault(); this.close(); });
    this._backdrop?.addEventListener('click', () => this.close());
    this._searchInput?.addEventListener('input', e => this._onSearchInput(e));

    document.addEventListener('click', e => {
      const btn = e.target.closest('.spt__btn--compare');
      if (!btn) return;
      this._openWithProduct({
        id:        btn.dataset.productId,
        handle:    btn.dataset.productHandle,
        title:     btn.dataset.productTitle,
        price:     btn.dataset.productPrice,
        image:     btn.dataset.productImage,
        url:       btn.dataset.productUrl,
        available: btn.dataset.productAvailable === 'true',
        variantId: btn.dataset.variantId,
        rating:    btn.dataset.rating,
      });
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });

    this.addEventListener('click', e => {
      const cartBtn = e.target.closest('.spt-compare__action-btn--cart');
      if (cartBtn && !cartBtn.disabled) {
        sptAddToCart(cartBtn.dataset.variantId, cartBtn);
        return;
      }
      const removeBtn = e.target.closest('.spt-compare__action-btn--remove');
      if (removeBtn) {
        this._productB = null;
        this._renderTable();
        this._toggleSearchPanel(true);
      }
    });
  }

  _openWithProduct(product) {
    this._productA = product;
    this._productB = null;
    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    this._clearSearch();
    this._toggleSearchPanel(true);
    this._renderTable();
    clearTimeout(this._focusTimeout);
    this._focusTimeout = setTimeout(() => this._searchInput?.focus(), 100);
  }

  close() {
    clearTimeout(this._focusTimeout);
    this._searchInput?.blur();
    this.setAttribute('hidden', '');
    document.body.style.overflow = '';
    this._clearSearch();
  }

  _toggleSearchPanel(show) {
    if (show) this._searchPanel?.removeAttribute('hidden');
    else      this._searchPanel?.setAttribute('hidden', '');
  }

  _clearSearch() {
    if (this._searchInput) this._searchInput.value = '';
    if (this._resultsList) {
      this._resultsList.innerHTML = '';
      this._resultsList.setAttribute('hidden', '');
    }
  }

  _onSearchInput(e) {
    clearTimeout(this._searchTimeout);
    const q = e.target.value.trim();
    if (q.length < 2) {
      this._resultsList.setAttribute('hidden', '');
      this._resultsList.innerHTML = '';
      return;
    }
    this._searchTimeout = setTimeout(() => this._doSearch(q), 320);
  }

  async _doSearch(query) {
    this._resultsList.removeAttribute('hidden');
    this._resultsList.innerHTML = `<li class="spt-compare__loading">Searching…</li>`;
    try {
      const res      = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`);
      const data     = await res.json();
      const products = data?.resources?.results?.products || [];
      this._renderResults(products);
    } catch {
      this._resultsList.innerHTML = `<li class="spt-compare__result-no">Search unavailable. Try again.</li>`;
    }
  }

  _renderResults(products) {
    if (!products.length) {
      this._resultsList.innerHTML = `<li class="spt-compare__result-no">No products found.</li>`;
      return;
    }
    this._resultsList.innerHTML = products.map(p => {
      const img   = p.image || '';
      const price = p.price ? this._formatMoney(p.price) : '';
      return `
        <li class="spt-compare__result-item" role="option" tabindex="0"
          data-handle="${p.handle}"
          data-title="${this._esc(p.title)}"
          data-price="${this._esc(price)}"
          data-image="${this._esc(img)}"
          data-url="${this._esc(p.url)}"
          data-id="${p.id || ''}"
        >
          ${img ? `<img class="spt-compare__result-img" src="${img}" alt="${this._esc(p.title)}" loading="lazy">` : ''}
          <div class="spt-compare__result-info">
            <div class="spt-compare__result-name">${this._esc(p.title)}</div>
            ${price ? `<div class="spt-compare__result-price">${price}</div>` : ''}
          </div>
        </li>`;
    }).join('');

    this._resultsList.querySelectorAll('.spt-compare__result-item').forEach(item => {
      const select = () => this._selectProductB(item);
      item.addEventListener('click', select);
      item.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') select(); });
    });
  }

  async _selectProductB(item) {
    const handle = item.dataset.handle;
    let variantId = '', available = false;
    try {
      const res  = await fetch(`/products/${handle}.js`);
      const data = await res.json();
      const v    = data.variants?.[0];
      variantId  = v?.id || '';
      available  = v?.available || false;
    } catch { /* silent */ }

    this._productB = {
      title:     item.dataset.title,
      price:     item.dataset.price,
      image:     item.dataset.image,
      url:       item.dataset.url,
      available,
      variantId: String(variantId),
      rating:    '5.0',
    };

    this._clearSearch();
    this._toggleSearchPanel(false);
    this._renderTable();
  }

  _renderTable() {
    const a = this._productA, b = this._productB;
    if (!a) return;

    if (b) {
      this._tableWrapper?.removeAttribute('hidden');
    } else {
      this._tableWrapper?.setAttribute('hidden', '');
      return;
    }

    this._fillCell(this._cells.imgA,     this._imgHTML(a.image, a.title));
    this._fillCell(this._cells.nameA,    `<a href="${a.url}" style="color:var(--spt-navy);font-weight:500;text-decoration:none">${this._esc(a.title)}</a>`);
    this._fillCell(this._cells.priceA,   `<strong>${this._esc(a.price)}</strong>`);
    this._fillCell(this._cells.availA,   this._availBadge(a.available));
    this._fillCell(this._cells.ratingA,  this._starsHTML(a.rating));
    this._fillCell(this._cells.actionsA, this._actionsHTML(a));

    this._fillCell(this._cells.imgB,     this._imgHTML(b.image, b.title));
    this._fillCell(this._cells.nameB,    `<a href="${b.url}" style="color:var(--spt-navy);font-weight:500;text-decoration:none">${this._esc(b.title)}</a>`);
    this._fillCell(this._cells.priceB,   `<strong>${this._esc(b.price)}</strong>`);
    this._fillCell(this._cells.availB,   this._availBadge(b.available));
    this._fillCell(this._cells.ratingB,  this._starsHTML(b.rating));
    this._fillCell(this._cells.actionsB, this._actionsHTML(b, true));
  }

  _fillCell(cell, html) { if (cell) cell.innerHTML = html; }

  _imgHTML(src, alt) {
    if (!src) return '<div style="width:100%;aspect-ratio:1/1;background:#f0f2f5;border-radius:8px;"></div>';
    return `<img class="spt-compare__prod-img" src="${src}" alt="${this._esc(alt)}" loading="lazy">`;
  }

  _availBadge(available) {
    return available
      ? `<span class="spt-compare__badge spt-compare__badge--in">✓ In Stock</span>`
      : `<span class="spt-compare__badge spt-compare__badge--out">✕ Out of Stock</span>`;
  }

  _starsHTML(rating) {
    const r = parseFloat(rating) || 5;
    let s = '<span class="spt-compare__stars">';
    for (let i = 1; i <= 5; i++) {
      s += i <= r
        ? `<svg width="13" height="13" viewBox="0 0 12 12" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M6 0.5L7.545 4.205L11.5 4.635L8.75 7.15L9.545 11.105L6 9.065L2.455 11.105L3.25 7.15L0.5 4.635L4.455 4.205L6 0.5Z"/></svg>`
        : `<svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="0.8" xmlns="http://www.w3.org/2000/svg"><path d="M6 0.5L7.545 4.205L11.5 4.635L8.75 7.15L9.545 11.105L6 9.065L2.455 11.105L3.25 7.15L0.5 4.635L4.455 4.205L6 0.5Z"/></svg>`;
    }
    return s + `</span> <span style="font-size:13px;font-weight:500">${r.toFixed(1)}</span>`;
  }

  _actionsHTML(product, isB = false) {
    const cartBtn = product.available
      ? `<button class="spt-compare__action-btn spt-compare__action-btn--cart" data-variant-id="${product.variantId}"><span class="spt__btn-text">Add to Cart</span></button>`
      : `<button class="spt-compare__action-btn spt-compare__action-btn--cart" disabled style="opacity:0.45;cursor:not-allowed"><span class="spt__btn-text">Out of Stock</span></button>`;
    const removeBtn = isB ? `<button class="spt-compare__action-btn spt-compare__action-btn--remove">Remove</button>` : '';
    return cartBtn + removeBtn;
  }

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  _formatMoney(amount) {
    if (!amount && amount !== 0) return '';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    const symbol = window.Shopify?.currency?.symbol || '£';
    const currency = window.Shopify?.currency?.active || 'GBP';
    return `${symbol}${num.toFixed(2)} ${currency}`;
  }
}

customElements.define('product-compare', ProductCompare);


/* ============================================================
   Tab Switching
   ============================================================ */
document.querySelectorAll('.spt__tabs').forEach(tabsEl => {
  tabsEl.addEventListener('click', e => {
    const tab = e.target.closest('.spt__tab');
    if (!tab) return;

    const section = tabsEl.closest('.spt');
    if (!section) return;

    section.querySelectorAll('.spt__tab').forEach(t => {
      t.classList.remove('spt__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    section.querySelectorAll('.spt__panel').forEach(p => {
      p.classList.remove('spt__panel--active');
      p.setAttribute('hidden', '');
    });

    tab.classList.add('spt__tab--active');
    tab.setAttribute('aria-selected', 'true');

    const panelId = tab.getAttribute('aria-controls');
    const panel   = section.querySelector(`#${panelId}`);
    if (panel) {
      panel.classList.add('spt__panel--active');
      panel.removeAttribute('hidden');
      const slider = panel.querySelector('product-slider');
      if (slider?._wrapper) {
        // Reset scroll position when switching tabs
        slider._wrapper.scrollLeft = 0;
        slider._updateArrows();
      }
    }
  });
});